import { Calendar, MapPin, Pause, Play } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { formatEventDate } from "components/EventMeta";

import { splitHeadline } from "../events/landing";
import type { PublicEvent } from "../events/schemas";
import { formatClock } from "./agenda-schedule";
import { shouldAutoplayHeroVideo } from "./hero-motion";
import { selectNowNext } from "./now-next";
import { useLiveAgenda } from "./use-event-content";
import { useNow } from "./use-now";

/** The widest a character is, as a share of its font size, in Playfair. */
const HEADLINE_CHARACTER_WIDTH = 0.62;
const HEADLINE_MIN_REM = 2.25;
const HEADLINE_MAX_REM = 8.5;

/**
 * The headline's size, from the width of the hero and its longest line.
 *
 * The container-query unit is the hero's own width, so a narrow column beside
 * the desktop rail shrinks the type as a narrow phone does, where `vw` would
 * not. The floor keeps "Innovation" legible at 320px; a longer line wraps.
 */
export function headlineFontSize(lines: readonly string[]): string {
  const longest = Math.max(4, ...lines.map((line) => line.length));
  return `clamp(${HEADLINE_MIN_REM}rem, calc((100cqi - 2.5rem) / ${(
    longest * HEADLINE_CHARACTER_WIDTH
  ).toFixed(2)}), ${HEADLINE_MAX_REM}rem)`;
}

// The overlay is what makes the white type readable over a clip or a photo.
// At 0.72 of Warm Grey, white text has at least 6:1 even over a pure white
// frame; the top is lighter because nothing is written there.
export const HERO_OVERLAY =
  "linear-gradient(180deg, hsl(var(--hb-warm-grey) / 0.4) 0%, hsl(var(--hb-warm-grey) / 0.72) 35%, hsl(var(--hb-warm-grey) / 0.86) 100%)";

const ON_DARK_FOCUS =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-hb-dusk-deep";

/**
 * The bold opening of the Overview: the event image or a muted loop behind a
 * dark overlay, the kicker, the stacked headline with its last line in the
 * accent, the subline, and the place and dates.
 *
 * The page's one h1 is the event title in the app header, so the headline is
 * an h2. Everything works with no clip; the clip is decoration.
 */
export function EventHero({
  event,
  myTrackId,
  onOpenNow,
}: {
  event: PublicEvent;
  myTrackId?: string;
  /** Opens the "Now" tab. Absent when the event has it switched off. */
  onOpenNow?: () => void;
}) {
  const { lines, accentIndex } = splitHeadline(event.heroHeadline);
  const kicker = event.heroKicker?.trim();
  const subline = event.heroSubline?.trim();
  const objectPosition = `${event.imageFocalX ?? 50}% ${event.imageFocalY ?? 50}%`;
  const video = event.heroVideo?.trim();
  const mediaBehind = Boolean(video || event.image);

  return (
    <div
      className="relative isolate flex min-h-[min(48rem,calc(100svh-9rem))] flex-col justify-end overflow-hidden rounded-2xl border border-border bg-dusk-hero dark:border-edge dark:border-lit-edge dark:shadow-lit-strong pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] pt-16 text-white sm:px-8"
      style={{ containerType: "inline-size" }}
      data-testid="event-hero"
    >
      {!video && event.image && (
        <img
          src={event.image}
          alt=""
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
          style={{ objectPosition }}
        />
      )}
      {/* The scrim is for a photo or a clip. Over the Dusk gradient alone it
          would only turn the hero black, and the gradient already carries the
          white type (9:1 and better). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={mediaBehind ? { background: HERO_OVERLAY } : undefined}
        data-testid="event-hero-overlay"
      />

      <div
        className={`relative z-20 flex flex-col items-start gap-4 sm:gap-5 ${video ? "pb-20" : "pb-7"}`}
      >
        {onOpenNow && (
          <LiveNowChip
            eventId={event.eventId}
            timeZone={event.timezone}
            myTrackId={myTrackId}
            onOpenNow={onOpenNow}
          />
        )}
        {kicker && (
          <p
            className="text-sm uppercase tracking-[0.16em] wrap-anywhere"
            data-testid="event-hero-kicker"
          >
            {kicker}
          </p>
        )}
        {lines.length > 0 && (
          <h2
            className="font-display font-normal leading-[0.9] tracking-tight wrap-anywhere"
            style={{ fontSize: headlineFontSize(lines) }}
          >
            {lines.map((line, index) =>
              index === accentIndex ? (
                <em
                  key={index}
                  className="block font-semibold italic text-hb-yellow"
                >
                  {line}
                </em>
              ) : (
                <span key={index} className="block">
                  {line}
                </span>
              ),
            )}
          </h2>
        )}
        {subline && (
          <p
            className="text-xl leading-snug wrap-anywhere sm:text-2xl"
            data-testid="event-hero-subline"
          >
            {subline}
          </p>
        )}
        <div
          className="flex flex-wrap gap-x-5 gap-y-2 text-base"
          data-testid="event-overview-meta"
        >
          {event.location && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-[18px] w-[18px] shrink-0" aria-hidden />
              {event.location}
            </span>
          )}
          {event.startDate && (
            <span className="flex items-center gap-1.5">
              <Calendar className="h-[18px] w-[18px] shrink-0" aria-hidden />
              {formatEventDate(event.startDate)}
              {event.endDate && event.endDate !== event.startDate && (
                <> &ndash; {formatEventDate(event.endDate)}</>
              )}
            </span>
          )}
        </div>
      </div>

      {video && (
        <HeroVideo
          // A new file is a new element, so the play state and the autoplay
          // decision start over with it.
          key={video}
          src={video}
          poster={event.image ?? undefined}
          objectPosition={objectPosition}
        />
      )}
    </div>
  );
}

/**
 * "Now · Keynote · 09:45", only while a session is running on the event's
 * clock. Reads the live agenda like the now-and-next card does, and re-reads
 * the clock every 30 seconds, so the chip goes when the session ends.
 */
function LiveNowChip({
  eventId,
  timeZone,
  myTrackId,
  onOpenNow,
}: {
  eventId: string;
  timeZone: string;
  myTrackId?: string;
  onOpenNow: () => void;
}) {
  const agenda = useLiveAgenda(eventId);
  const now = useNow();
  const myTrackIds = useMemo(
    () => new Set(myTrackId ? [myTrackId] : []),
    [myTrackId],
  );
  const state = useMemo(
    () =>
      agenda.status === "success"
        ? selectNowNext(agenda.data, now, myTrackIds, timeZone)
        : null,
    [agenda, now, myTrackIds, timeZone],
  );
  if (state?.phase !== "live") return null;

  const session = state.now[0];
  return (
    <button
      type="button"
      onClick={onOpenNow}
      className={`inline-flex min-h-11 max-w-full items-center gap-2 rounded-full border border-white/45 bg-white/15 px-3.5 text-sm text-white transition-colors hover:bg-white/25 ${ON_DARK_FOCUS}`}
      data-testid="event-hero-now"
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full bg-hb-yellow shadow-lit-dot"
      />
      <span className="min-w-0 truncate">
        Now · {session.item.title} · {formatClock(session.start, timeZone)}
      </span>
    </button>
  );
}

/**
 * The muted loop and its pause button.
 *
 * It starts by itself only when `shouldAutoplayHeroVideo` allows, and a
 * browser that refuses (a rejected play(), low-power mode) leaves the poster
 * and a paused button instead of an error. The button is a real 44px control
 * because the clip loops for longer than five seconds (WCAG 2.2.2). Its label
 * stays "Pause video" and `aria-pressed` says whether it is paused, so the
 * name does not flip with the state.
 */
export function HeroVideo({
  src,
  poster,
  objectPosition,
  toggleTestId = "event-hero-video-toggle",
}: {
  src: string;
  poster?: string;
  objectPosition: string;
  /** The member hero and the public hero each name their own pause button. */
  toggleTestId?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [autoplay] = useState(shouldAutoplayHeroVideo);
  const [playing, setPlaying] = useState(false);

  function start() {
    const element = ref.current;
    if (!element) return;
    // play() returns nothing in some old engines and rejects when the browser
    // declines to play; neither is an error for the page.
    void Promise.resolve(element.play()).then(
      () => setPlaying(true),
      () => setPlaying(false),
    );
  }

  useEffect(() => {
    if (autoplay) start();
    // Once, on mount: whether to autoplay is decided when the page opens.
  }, []);

  function toggle() {
    if (playing) ref.current?.pause();
    else start();
  }

  return (
    <>
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        autoPlay={autoplay}
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
        style={{ objectPosition }}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label="Pause video"
        aria-pressed={!playing}
        className={`absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-[max(0.75rem,env(safe-area-inset-right))] z-30 flex h-11 w-11 items-center justify-center rounded-full border border-white/45 bg-hb-warm-grey/50 text-white transition-colors hover:bg-hb-warm-grey/70 ${ON_DARK_FOCUS}`}
        data-testid={toggleTestId}
      >
        {playing ? (
          <Pause className="h-4 w-4 fill-current" aria-hidden />
        ) : (
          <Play className="h-4 w-4 fill-current" aria-hidden />
        )}
      </button>
    </>
  );
}
